import React, { useEffect, useState } from "react";

// components
import Header from "components/Header";
import Footer from "components/Footer";
import SEO from "components/SEO";
import TOC from "components/TOC";
import NoPurchase from "components/NoPurchase";
import Disclaimer from "components/Disclaimer";
import GameMechanics from "components/GameMechanics";
import ReCAPTCHA from "react-google-recaptcha";

import { CLAIM_STATES } from "../libs/state";
import { isBrowser } from "../libs/isBrowser";

// assets
import "normalize.css";
import "src/styles/main.scss";

// data
import data from "content/data.json";

const ARTISTS = data["artists"];
const IS_ACTIVE = data["isActive"];
const DROP_DATE = data["dropDate"];
const RECAPTCHA_CODE = data["reCaptcha"];
const RECAPTCHA_CODE_NP = data["reCaptchaNP"];
const cardTypes = ["Flippable Card", "Real Signature", "Behind the Scenes"];

let availableClaimCodes = [];
ARTISTS.map((item) => {
  item["claimCode"].map((code) => availableClaimCodes.push(code));
});

const sendToApp = (claimCode, upstreamOS, currentUrl) => {
  let appSchema;
  if (upstreamOS == "ios") {
    appSchema = "sweet:///collectible/redeem/" + claimCode;
  } else if (upstreamOS == "android") {
    appSchema =
      "intent:///collectible/redeem/" +
      claimCode +
      "#Intent;scheme=sweet;package=io.sweet.app;S.browser_fallback_url=http%3A%2F%2F" +
      currentUrl +
      "%2F%3Fclaim%3D" +
      claimCode +
      "%26bpa%3Dtrue%26dev%3Dandroid;S.branch_data=%22%24deeplink_path%22%3A%22%2Fcollectible%2Fredeem%2F" +
      claimCode +
      "%22%7D;end";
  }
  window.location.href = appSchema;
};

const sendBackBypassApp = (url, claimCode, showRules) => {
  let reloadUrl = new URL(url);
  reloadUrl.searchParams.append("claim", claimCode);
  reloadUrl.searchParams.append("bpa", true);
  if (showRules) reloadUrl = reloadUrl + "#rules";
  window.location.href = reloadUrl;
};

const isMobileSafari = () => {
  let isMobileSafari = true;
  if (isBrowser) {
    if (navigator.userAgent.includes('CriOS')) {
      isMobileSafari = false;
    }
  }
  return isMobileSafari;
}

const recaptchaRef = React.createRef();

const IndexPage = () => {
  const html = isBrowser ? document.querySelector("html") : null;

  const [isActive, setIsActive] = useState(IS_ACTIVE);

  const [isTOCModalVisible, setIsTOCModalVisible] = useState(false);
  const [isNPModalVisible, setIsNPModalVisible] = useState(false);

  const toggleTOCModal = () => {
    setIsTOCModalVisible(!isTOCModalVisible);
    if (html) {
      if (isTOCModalVisible == true) {
        html.classList.remove("noscroll");
      } else {
        html.classList.add("noscroll");
      }
    }
  };

  const toggleNPModal = () => {
    setIsNPModalVisible(!isNPModalVisible);
    if (html) {
      if (isNPModalVisible == true) {
        html.classList.remove("noscroll");
      } else {
        html.classList.add("noscroll");
      }
    }
  };

  const [claimState, setClaimState] = useState(
    isActive ? CLAIM_STATES[0] : CLAIM_STATES[6]
  );
  const [currentUrl, setCurrentUrl] = useState(null);
  const [currentArtist, setCurrentArtist] = useState(ARTISTS[0]);
  const [urlParams, setUrlParams] = useState(null);
  const [npRegisteredEmail, setNpRegisteredEmail] = useState(null);

  const [mainCaptchaToken, setMainCaptchaToken] = useState(null);

  const validateMainCaptcha = async () => {
    try {
      const token = await recaptchaRef.current.executeAsync();
      if (token) {
        setMainCaptchaToken(token);
        urlParams
          ? setClaimState(CLAIM_STATES[2])
          : setClaimState(CLAIM_STATES[4]);
      }
    } catch (error) {
      console.log("Captcha error: ", error);
    }
  };

  const handleNoPurchaseResponse = (response) => {
    const noPurchaseArtist = ARTISTS.filter((obj) => {
      return obj.id === response.id;
    });
    setCurrentArtist(noPurchaseArtist[0]);
    if (isBrowser) {
      window.scrollTo(0, 0);
    }
    toggleNPModal();
    setClaimState(CLAIM_STATES[5]);
  };

  useEffect(() => {
    if (isBrowser) {
      window.SWT_WEB_LAND = true;

      setCurrentUrl(window.location.hostname);
      const showRules = window.location.hash.includes("rules");

      const urlParams = new URLSearchParams(window.location.search);
      const claimCode = urlParams.get("claim");
      if (urlParams && availableClaimCodes.includes(claimCode)) {
        setUrlParams(claimCode);
        ARTISTS.map((item, index) => {
          item["claimCode"].find((code, _) => {
            if (claimCode === code) {
              setCurrentArtist(ARTISTS[index]);
            }
          });
        });
      }
      if (isActive) {
        if (claimCode != null) {
          if (urlParams.get("dev") && urlParams.get("dev") != "") {
            const upstream = urlParams.get("dev");
            if (upstream == "android" && urlParams.get("force_deeplink")) {
              sendToApp(claimCode, "android", window.location.host);
            } else if (upstream == "ios") {
              if (!urlParams.get("bpa")) {
                // adding force_deeplink for testing
                if (!isMobileSafari || urlParams.get("force_deeplink")) {
                  window.addEventListener(
                    "load", 
                    sendToApp(claimCode, "ios", window.location.host)
                  );
                }
                const redirectTimer = setTimeout(() => {
                  sendBackBypassApp(
                    window.location.origin,
                    claimCode,
                    showRules
                  );
                }, 250);
                return () => clearTimeout(redirectTimer);
              }
            }
          }
        }
        if (showRules) {
          toggleTOCModal();
        }
      }
    }
  }, []);

  return (
    <main className="container">
      <SEO />

      <Header
        dropDate={DROP_DATE}
        currentUrl={currentUrl}
        onRulesClick={() => toggleTOCModal()}
        claimState={claimState}
        currentArtist={currentArtist}
        registeredEmail={npRegisteredEmail}
        recaptchaCode={RECAPTCHA_CODE_NP}
      />
      {isActive &&
        claimState !== CLAIM_STATES[3] &&
        claimState !== CLAIM_STATES[4] &&
        claimState !== CLAIM_STATES[5] &&
        claimState !== CLAIM_STATES[7] && (
          <>
            <div
              className="artists__wrapper"
              data-current-artist={currentArtist["id"]}>
              <div className="claim">
                {(() => {
                  switch (claimState) {
                    case CLAIM_STATES[1]:
                      return (
                        <>
                          <ReCAPTCHA
                            ref={recaptchaRef}
                            size="invisible"
                            sitekey={RECAPTCHA_CODE}
                          />
                          <div className="claim__recaptcha">
                            <button
                              data-checked={mainCaptchaToken ? true : false}
                              onClick={() => validateMainCaptcha()}>
                              I'm not a robot
                            </button>
                          </div>
                        </>
                      );
                    case CLAIM_STATES[2]:
                      return (
                        <div className="claim__rules">
                          <button
                            onClick={() => {
                              setClaimState(CLAIM_STATES[3]);
                            }}
                            className="button claim__button">
                            Accept rules and claim!
                          </button>
                        </div>
                      );
                    default:
                      return (
                        <button
                          onClick={() => setClaimState(CLAIM_STATES[1])}
                          className="button claim__button">
                          Start collecting now!
                        </button>
                      );
                  }
                })()}
              </div>
              <Disclaimer
                currentUrl={currentUrl}
                onRulesClick={() => toggleTOCModal()}
              />
              <ul className="artists__tabs">
                {ARTISTS.map((artist, index) => {
                  return (
                    <li
                      role="button"
                      className="artist__trigger"
                      key={index}
                      onClick={() => setCurrentArtist(artist)}
                      data-artist={artist.id}>
                      <span className="artist__avatar">{artist.alias}</span>
                    </li>
                  );
                })}
              </ul>
              <div className="artists__content">
                {ARTISTS.map((artist, index) => {
                  return (
                    <div
                      className="artist__slide"
                      key={index}
                      data-artist={artist.id}>
                      <div className="artist__container">
                        <h3 className="artist__name">
                          <strong>{artist.name}</strong> aka {artist.alias}{" "}
                          <strong>set</strong>
                        </h3>
                        <ul className="artist__cards">
                          {cardTypes.map((item, index) => {
                            return (
                              <li
                                key={index}
                                className="artist__card"
                                data-card={index}>
                                <div className="artist__card-flip">
                                  <span className="artist__card--front" />
                                  <span className="artist__card--back" />
                                </div>
                                <div className="artist__card-name">{item}</div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  );
                })}
              </div>
              <GameMechanics artists={ARTISTS} />
            </div>
          </>
        )}
      <div className="legal-rules">
        {isActive && (
          <>
            <h2 className="scan">
              Scan every 12 hours to collect a game piece!
            </h2>
            <a
              className="button"
              data-size="md"
              onClick={() => toggleTOCModal()}>
              Read the official rules
            </a>
          </>
        )}
        <small>
          TM & (C) 2021 Burger King Corporation. Used under license. All rights
          reserved.
        </small>
      </div>
      {isTOCModalVisible && (
        <TOC
          isActive={isActive}
          openTOC={() => toggleTOCModal()}
          openNP={() => {
            toggleNPModal();
            setIsTOCModalVisible(false);
          }}
          isVisible={isTOCModalVisible}
          showAcceptButton={claimState === CLAIM_STATES[2] ? true : false}
          onAccept={
            claimState === CLAIM_STATES[2]
              ? () => {
                  toggleTOCModal();
                  setClaimState(CLAIM_STATES[3]);
                }
              : null
          }
        />
      )}
      {isNPModalVisible && (
        <NoPurchase
          artists={ARTISTS}
          openNP={() => toggleNPModal()}
          openTOC={() => {
            toggleTOCModal();
            setIsNPModalVisible(false);
          }}
          isVisible={isNPModalVisible}
          recaptchaRef={recaptchaRef}
          recaptchaCode={RECAPTCHA_CODE_NP}
          noPurchaseClaim={handleNoPurchaseResponse}
        />
      )}
      <Footer />
    </main>
  );
};

export default IndexPage;
